import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, MessageSquare, Send, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

/** Admin SMS Panel — appears in Event Hub → Overview. Shows Twilio config health,
 * recent send counts, a test-send widget, and the last 10 log entries. */
export default function SmsAdminPanel() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Business Showers SMS test — reply STOP to opt out.");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/event/admin/sms/status"),
        api.get("/event/admin/sms/logs", { params: { limit: 15 } }),
      ]);
      setStatus(s.data);
      setLogs(l.data.logs || []);
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a phone number"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/event/admin/sms/test", { to: testTo, body: testBody });
      if (data.status === "sent") toast.success(`SMS sent — SID ${data.sid?.slice(0, 10)}…`);
      else if (data.status?.startsWith("skipped_")) toast.warning(data.error || data.status);
      else toast.error(data.error || `Twilio returned: ${data.status}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  if (loading) return <div className="p-4 text-center"><Loader2 className="mx-auto animate-spin" /></div>;

  if (!status) {
    return (
      <Card data-testid="sms-admin-panel">
        <CardContent className="p-4">
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-sm font-[Outfit] text-amber-900">
            <div className="font-bold flex items-center gap-2">
              <AlertTriangle size={16} /> SMS status unavailable
            </div>
            <div className="mt-1 text-xs">
              Could not reach the SMS status endpoint. This usually means the backend just redeployed and hasn't finished restarting.
            </div>
            <Button
              onClick={load}
              className="mt-3 rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const ready = status?.ready_to_send;
  const partial = status?.account_sid_configured && status?.auth_token_configured && !status?.sender_ready;

  return (
    <Card data-testid="sms-admin-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-fuchsia-700" />
          <h3 className="font-[Cormorant_Garamond] text-xl">SMS (Twilio)</h3>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status banner */}
        <div
          data-testid="sms-status-banner"
          className={`rounded-xl p-3 border-2 text-sm font-[Outfit] ${
            ready
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : partial
                ? "bg-amber-50 border-amber-200 text-amber-900"
                : "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900"
          }`}
        >
          <div className="flex items-start gap-2">
            {ready ? <CheckCircle2 size={18} className="mt-0.5" /> : <AlertTriangle size={18} className="mt-0.5" />}
            <div className="flex-1">
              <div className="font-bold">
                {ready ? "Ready to send" : partial ? "Awaiting Twilio phone number" : "Twilio credentials missing"}
              </div>
              <ul className="mt-1 text-xs space-y-0.5">
                <li>
                  Account SID: {status.account_sid_configured
                    ? <span className="font-mono text-[11px]">{status.account_sid_prefix}</span>
                    : <span className="opacity-60">not configured</span>}
                </li>
                <li>
                  Auth Token: {status.auth_token_configured ? "configured" : <span className="opacity-60">not configured</span>}
                </li>
                <li>
                  Sender: {status.from_number
                    ? <span className="font-mono text-[11px]">{status.from_number}</span>
                    : status.messaging_service_sid
                      ? <span className="font-mono text-[11px]">{status.messaging_service_sid}</span>
                      : <span className="opacity-60">pending — add TWILIO_FROM_NUMBER (or TWILIO_MESSAGING_SERVICE_SID) to backend/.env when compliance clears</span>}
                </li>
                <li>Admin toggle: <span className="font-semibold">{status.admin_toggle_enabled ? "ON" : "OFF"}</span> (Settings → SMS confirmation)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-4 gap-2">
          <MiniStat label="Total" value={status.counts?.total ?? 0} />
          <MiniStat label="Sent" value={status.counts?.sent ?? 0} accent="emerald" />
          <MiniStat label="Failed" value={status.counts?.failed ?? 0} accent="fuchsia" />
          <MiniStat label="Skipped" value={status.counts?.skipped ?? 0} />
        </div>

        {/* Test send */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Send a test message</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="sms-test-to"
              placeholder="+17045550100 or 704-555-0100"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className="sm:max-w-xs"
            />
            <Input
              data-testid="sms-test-body"
              placeholder="Message body"
              value={testBody}
              onChange={(e) => setTestBody(e.target.value)}
              maxLength={320}
            />
            <Button
              onClick={sendTest}
              disabled={sending || !ready}
              data-testid="sms-test-send"
              className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-10 whitespace-nowrap"
            >
              {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
              Send test
            </Button>
          </div>
          {!ready && (
            <div className="text-[11px] font-[Outfit] text-emerald-800/60">
              Test send is disabled until a Twilio number is added to <code className="bg-emerald-100 px-1 rounded">backend/.env</code>.
            </div>
          )}
        </div>

        {/* Recent log */}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60 mb-1">Recent activity</div>
          {logs.length === 0 ? (
            <div className="text-xs text-emerald-800/60 font-[Outfit] italic">No SMS attempts yet.</div>
          ) : (
            <ul data-testid="sms-log-list" className="divide-y divide-emerald-100 rounded-lg border border-emerald-100 overflow-hidden">
              {logs.map((l) => (
                <li key={l.id} className="px-3 py-2 flex items-center gap-2 text-xs font-[Outfit]">
                  <StatusBadge status={l.status} />
                  <span className="text-emerald-950/70">···-{l.to_last4 || "----"}</span>
                  <span className="text-emerald-950 flex-1 truncate">{l.body_preview || l.error || l.kind}</span>
                  <span className="text-emerald-950/40 text-[10px] whitespace-nowrap flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(l.sent_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, accent }) {
  const cls = accent === "emerald"
    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
    : accent === "fuchsia"
      ? "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-900"
      : "border-emerald-100 bg-white text-emerald-950";
  return (
    <div className={`rounded-lg border-2 ${cls} p-2 text-center`}>
      <div className="text-[9px] uppercase tracking-widest font-[Outfit] font-bold opacity-60">{label}</div>
      <div className="font-[Cormorant_Garamond] text-xl leading-none mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: ["bg-emerald-100 text-emerald-800", "Sent"],
    failed: ["bg-red-100 text-red-800", "Failed"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-700", (status || "").replace("skipped_", "skip: ")];
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${cls}`}>
      {label}
    </span>
  );
}
